import { UploadOutlined } from "@ant-design/icons";
import { Button, Col, Row, Space, Upload } from "antd";
import { useEffect, useState } from "react";
import PropTypes from "prop-types";

import useSimplePromptStudio from "../hooks/useSimplePromptStudio";
import { useCustomToolStore } from "../../store/custom-tool-store";
import {
  deleteDocumentSps,
  uploadDocuments,
} from "./simple-prompt-studio-api-service";
import { useAlertStore } from "../../store/alert-store";
import { useExceptionHandler } from "../../hooks/useExceptionHandler";
import { RecaptchaModal } from "../recaptcha/RecaptchaModal";
import { LANDING } from "./constants";

function ManageDocs({ isModal, setOpen, handleDocAdd }) {
  const [fileList, setFileList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openCaptchaModal, setOpenCaptchaModal] = useState(false);
  const { handleSpsNavigate } = useSimplePromptStudio();
  const { details, listOfDocs, selectedDoc, updateCustomTool } =
    useCustomToolStore();
  const { setAlertDetails } = useAlertStore();
  const handleException = useExceptionHandler();

  useEffect(() => {
    setFileListWithListOfDocs(listOfDocs);
  }, []);

  const setFileListWithListOfDocs = (docs) => {
    const modifiedList = docs.map((item) => {
      return {
        name: item?.document_name,
        document_id: item?.document_id,
      };
    });
    setFileList(modifiedList);
  };

  const handleUpload = ({ file, fileList }) => {
    setFileList(fileList.slice(-5));
  };

  const handleUploadDocuments = () => {
    if (fileList.length === 0) {
      setAlertDetails({
        type: "error",
        content: "Please upload atleast one document",
      });
      return;
    }

    setOpenCaptchaModal(true);
  };

  const handleSubmit = async (token) => {
    setIsLoading(true);
    const formData = new FormData();
    let fileAvailableToUpload = false;
    fileList.forEach((file) => {
      if (file?.document_id) {
        return;
      }

      if (!fileAvailableToUpload) fileAvailableToUpload = true;
      formData.append("file", file.originFileObj);
    });

    if (!fileAvailableToUpload) {
      setIsLoading(false);
      if (!isModal) handleSpsNavigate("");
      return;
    }

    const projectId = details?.tool_id;
    formData.append("sps_project_id", projectId);
    formData.append("recaptcha_token", token);
    uploadDocuments(formData)
      .then((res) => {
        const data = res?.data?.data;
        const newListOfDocs = [...listOfDocs, ...data];
        updateCustomTool({ listOfDocs: newListOfDocs });
        setFileListWithListOfDocs(newListOfDocs);
        if (isModal) {
          setOpen(false);
          handleSelectedDocChange(newListOfDocs);
          handleDocAdd(data[0]);
        } else {
          handleSpsNavigate("");
        }
      })
      .catch((err) => {
        setAlertDetails(handleException(err, "Failed to upload documents"));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSelectedDocChange = (docs) => {
    // Check if there are any documents to handle
    if (docs.length === 0) {
      updateCustomTool({ selectedDoc: null });
      return;
    }

    // If no document is selected or the selected document is not in the new list, select the first document
    if (
      !selectedDoc ||
      !docs.find((doc) => doc?.document_id === selectedDoc?.document_id)
    ) {
      setFileList;
      updateCustomTool({ selectedDoc: docs[0] });
    }
  };

  const handleDocDelete = async (file) => {
    if (!file?.document_id) {
      return true;
    }

    return deleteDocumentSps(file?.document_id)
      .then(() => {
        updateListOfDocs(file?.document_id);
        setAlertDetails({
          type: "success",
          content: `Deleted successfully: ${file?.name}`,
        });
        return true;
      })
      .catch((err) => {
        setAlertDetails(
          handleException(err, `Failed to delete document: ${file?.name}`)
        );
        return false;
      });
  };

  const updateListOfDocs = (documentId) => {
    // Filter the list to exclude the document with the given documentId
    const newListOfDocs = listOfDocs.filter(
      (doc) => doc?.document_id !== documentId
    );

    // Determine the new selected document
    let newSelectedDoc =
      selectedDoc?.document_id === documentId ? null : selectedDoc;

    // If no selected document and there are documents left, select the first one
    if (!newSelectedDoc && newListOfDocs.length) {
      newSelectedDoc = newListOfDocs[0];
    }

    // Update the custom tool with the new list and selected document
    updateCustomTool({
      listOfDocs: newListOfDocs,
      selectedDoc: newSelectedDoc,
    });
  };

  const beforeUpload = (file) => {
    const isDuplicate = fileList.some(
      (existingFile) => existingFile.name === file.name
    );
    if (isDuplicate) {
      setAlertDetails(
        handleException(null, `${file.name} is already uploaded.`)
      );
      return true;
    }
    return false;
  };

  return (
    <Space direction="vertical" className="width-100">
      <Upload
        className="sps-upload"
        maxCount={5}
        multiple
        beforeUpload={beforeUpload}
        onChange={handleUpload}
        fileList={fileList}
        onRemove={handleDocDelete}
      >
        <Button icon={<UploadOutlined />} disabled={fileList?.length === 5}>
          Upload documents
        </Button>
      </Upload>
      <Row>
        {!isModal && (
          <Col span={12} className="pad-right-6">
            <Button block onClick={() => handleSpsNavigate(LANDING)}>
              Previous
            </Button>
          </Col>
        )}
        <Col span={isModal ? 24 : 12}>
          <Button
            type="primary"
            className="pad-left-6"
            block
            onClick={handleUploadDocuments}
            loading={isLoading}
          >
            Submit
          </Button>
        </Col>
      </Row>
      <RecaptchaModal
        open={openCaptchaModal}
        setOpen={setOpenCaptchaModal}
        onSuccess={handleSubmit}
      />
    </Space>
  );
}

ManageDocs.propTypes = {
  isModal: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  handleDocAdd: PropTypes.func.isRequired,
};

export { ManageDocs };
